import { Box, Card, CardContent, Chip, FormControlLabel, MenuItem, Skeleton, Switch, TextField, Typography } from '@mui/material';
import {
    AlertTriangle,
    Calendar,
    Check,
    ClipboardList,
    Package,
    Repeat,
    TrendingDown,
    TrendingUp,
    Wallet,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { httpClient } from '../../common/httpclient';
import { FilterChips } from '../../component/filter/FilterToolbar';
import ReportFilterBar from '../../component/report/ReportFilterBar';
import AnimatedNumber from '../../component/common/AnimatedNumber';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import DefaultTable from '../../component/table/DefaultTable';
import usePermissions from '../../hooks/usePermissions';
import '../reports/reports.css';
import { INVENTORY_STATUS, inventoryErrorMessage } from './inventoryStatus';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import { parseMoney } from '../../component/common/MobileDashboardKit';
import { KitEmpty, KitField, KitSelect, KitSwitch } from '../../component/common/MobileKitExtras';
import { protoIcon } from '../../component/common/ProtoIcon';
import MobileReport, { MobileReportBody, formatMoneyKpi, formatMoneyRank } from '../../component/report/MobileReport';
import { InventoryHeader, InventoryItemList } from '../../component/report/MobileInventorySections';
import { SecH } from '../../component/common/MobileKit';
import { Caption } from '../../component/report/MobileSalesSections';

// O backend devolve dinheiro como texto (ex.: "20,00", "-60,00", "+30,00"); aqui só prefixa "R$" mantendo o sinal na frente.
function formatBRL(value) {
    if (value === null || value === undefined || value === '') return '—';
    const text = String(value);
    const sign = text.startsWith('-') || text.startsWith('+') ? text[0] : '';
    return `${sign}R$ ${sign ? text.slice(1) : text}`;
}

const plainNumber = (n) => String(n);

const formatBR2 =(n) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Props do KpiCard para um valor monetário: anima (count up) o módulo mantendo o sinal e o
// "R$" idênticos ao `formatBRL`. Se o texto não for reproduzível, cai no valor estático.
function moneyKpiProps(value) {
    if (value === null || value === undefined || value === '') return { value: formatBRL(value) };
    const text = String(value);
    const sign = text.startsWith('-') || text.startsWith('+') ? text[0] : '';
    const body = sign ? text.slice(1) : text;
    const parsed = parseFloat(body.replace(/\./g, '').replace(',', '.'));
    if (!Number.isFinite(parsed) || formatBR2(parsed) !== body) return { value: formatBRL(value) };
    return { amount: parsed, decimals: 2, format: (n) => `${sign}R$ ${formatBR2(n)}` };
}

function formatDateTime(value) {
    if (!value) return '—';
    return new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

/**
 * DESIGN-SYSTEM.md §6.1 "report-toolbar" micro-label — uppercase, static,
 * fora do campo. Mesmo padrão local dos demais relatórios (`pages/reports/`).
 */
const TbLabel = ({ children }) => (
    <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
        {children}
    </Typography>
);

const TB_FIELD_RADIUS = '9px';
const selectFieldSx = {
    minWidth: 320,
    '& .MuiOutlinedInput-root': {
        height: 40,
        fontSize: '13px',
        borderRadius: TB_FIELD_RADIUS,
        backgroundColor: 'background.subtle',
    },
};

// KPI card — DESIGN-SYSTEM.md §5.2 "KPI card" (mesmo componente local dos demais relatórios).
// `valueColor` é opcional: só usado nos valores com sentido de perda/sobra/saldo.
// `amount` (número) + `format` => valor animado; sem eles, `value` como está.
function KpiCard({ icon, iconBg, iconFg, value, amount, format, decimals, label, loading, valueColor }) {
    return (
        <Card>
            {/* Variante compacta: ícone à esquerda, valor + legenda empilhados à direita. */}
            <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 }, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box sx={{
                    width: 28,
                    height: 28,
                    flexShrink: 0,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: iconBg,
                    color: iconFg,
                }}>
                    {icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    {loading ? (
                        <Skeleton variant="text" width={60} height={22} />
                    ) : (
                        <Typography sx={{ fontSize: '15px', fontWeight: 800, letterSpacing: '-0.02em', color: valueColor || 'text.primary', lineHeight: 1.2, overflowWrap: 'anywhere' }}>
                            {amount !== undefined
                                ? <AnimatedNumber value={amount} format={format} decimals={decimals} />
                                : value}
                        </Typography>
                    )}
                    <Typography sx={{ fontSize: '11px', color: 'text.secondary', lineHeight: 1.3 }}>
                        {label}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
}

// DESIGN-SYSTEM.md §5.11 empty state — mesmo bloco dos demais relatórios.
function EmptyState({ title, subtitle }) {
    return (
        <Card>
            <CardContent sx={{ py: 7, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5 }}>
                <Box sx={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    border: '2px dashed',
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.disabled',
                }}>
                    <ClipboardList size={30} strokeWidth={2} />
                </Box>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: 'text.secondary' }}>{title}</Typography>
                <Typography sx={{ fontSize: '12.5px', color: 'text.disabled', maxWidth: 380 }}>{subtitle}</Typography>
            </CardContent>
        </Card>
    );
}

const diffColor = (diff) => (diff > 0 ? 'success.main' : diff < 0 ? 'error.main' : 'text.secondary');

export default function Report() {
    const { hasPermission } = usePermissions();

    const [inventories, setInventories] = useState([]);
    const [id, setId] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [onlyDivergent, setOnlyDivergent] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    const notify = (message, severity = 'success') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setOpenSnackbar(true);
    };

    useEffect(() => {
        httpClient.get('/inventories')
            .then((response) => setInventories(Array.isArray(response.data) ? response.data : []))
            .catch((error) => {
                console.error('Error fetching inventories:', error);
                notify(inventoryErrorMessage(error, 'Erro ao carregar inventários.'), 'error');
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!id) {
            setData(null);
            return;
        }
        setLoading(true);
        httpClient.get(`/inventories/${id}/report`, { params: { format: 'json' } })
            .then((response) => setData(response.data))
            .catch((error) => {
                console.error('Error fetching inventory report:', error);
                notify(inventoryErrorMessage(error, 'Erro ao carregar relatório.'), 'error');
            })
            .finally(() => setLoading(false));
    }, [id]);

    const inventory = data?.inventory;
    const summary = data?.summary;
    const statusInfo = INVENTORY_STATUS[inventory?.status] || INVENTORY_STATUS.draft;

    const handleExport = async (format) => {
        try {
            await httpClient.downloadFile(`/inventories/${id}/report?format=${format}`, {
                title: `inventario_${inventory.code}`,
                extension: format,
            });
        } catch (error) {
            console.error('Error exporting inventory:', error);
            notify('Erro ao exportar relatório.', 'error');
        }
    };

    const items = (data?.items || []).filter((item) => !onlyDivergent || (item.difference !== null && item.difference !== 0));

    const columns = [
        { path: 'code', name: 'Código', invisible: false, render: (row) => <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>{row.code}</Typography> },
        { path: 'name', name: 'Produto', invisible: false, render: (row) => <Typography sx={{ fontSize: '13px' }}>{row.name}</Typography> },
        { path: 'expected_qty', name: 'Sistema', invisible: false, render: (row) => <Typography sx={{ fontSize: '13px' }}>{row.expected_qty}</Typography> },
        { path: 'counted_qty', name: 'Contado', invisible: false, render: (row) => <Typography sx={{ fontSize: '13px' }}>{row.counted_qty ?? '—'}</Typography> },
        {
            path: 'difference',
            name: 'Diferença',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: row.difference === null ? 'text.disabled' : diffColor(row.difference) }}>
                    {row.difference === null ? '—' : (row.difference > 0 ? `+${row.difference}` : row.difference)}
                </Typography>
            ),
        },
        { path: 'cost', name: 'Custo', invisible: false, render: (row) => <Typography sx={{ fontSize: '13px' }}>{formatBRL(row.cost)}</Typography> },
        {
            path: 'impact',
            name: 'Impacto',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: row.difference === null ? 'text.disabled' : diffColor(row.difference) }}>
                    {row.difference === null ? '—' : formatBRL(row.impact)}
                </Typography>
            ),
        },
    ];

    const netColor = summary?.net_value?.startsWith('-') ? 'error.main' : summary?.net_value?.startsWith('+') ? 'success.main' : undefined;
    const canView = hasPermission('report.inventory');
    const showKpis = Boolean(summary) || (loading && Boolean(id));

    const isMobileShell = useIsMobileShell();

    if (isMobileShell) {
        // "+1.234,56" / "-1.234,56" (texto da API) -> "+ R$ 1,2 mil" / "- R$ 1,2 mil"
        const signedShort = (value, format = formatMoneyKpi) => {
            const text = String(value ?? '');
            const sign = text.startsWith('-') || text.startsWith('+') ? text[0] : '';
            const amount = parseMoney(sign ? text.slice(1) : text);
            return `${sign ? `${sign} ` : ''}${format(amount)}`;
        };
        const divergent = items
            .filter((item) => item.difference !== null && item.difference !== 0)
            .map((item) => ({ item, impact: parseMoney(String(item.impact ?? '').replace(/^[+-]/, '')) }))
            .sort((a, b) => b.impact - a.impact)
            .slice(0, 3);
        const download = protoIcon('download');
        const filePdf = protoIcon('file');
        return (
            <PageScaffold
                title="Relatório de Inventário"
                titlePageTarget="Inventário"
                links={[{ title: 'Relatórios', link: '/reports' }]}
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
                message={snackbarMessage}
                severity={snackbarSeverity}
            >
                {!canView ? (
                    <KitEmpty icon={protoIcon('clipcheck')} title="Acesso restrito">Você não tem permissão para visualizar o relatório de inventário.</KitEmpty>
                ) : (
                    <MobileReport
                        onGenerate={() => {}}
                        onClear={() => { setId(''); setOnlyDivergent(false); }}
                        exportItems={inventory ? [
                            { label: 'PDF', icon: filePdf, onClick: () => handleExport('pdf') },
                            { label: 'Excel (CSV)', icon: download, onClick: () => handleExport('csv') },
                        ] : []}
                        extraFilters={(
                            <>
                                <KitField label="Inventário">
                                    <KitSelect value={id} onChange={(e) => setId(e.target.value)} aria-label="Inventário">
                                        <option value="">Selecione um inventário</option>
                                        {inventories.map((inv) => (
                                            <option key={inv.id} value={inv.id}>{`#${inv.code}${inv.description ? ` - ${inv.description}` : ''}`}</option>
                                        ))}
                                    </KitSelect>
                                </KitField>
                                <KitSwitch title="Divergências" description="Mostrar somente divergentes" checked={onlyDivergent} onChange={(e) => setOnlyDivergent(e.target.checked)} />
                            </>
                        )}
                    >
                        {inventory && (
                            <InventoryHeader
                                inventory={inventory}
                                statusInfo={statusInfo}
                                createdAt={formatDateTime(inventory.created_at)}
                                finishedAt={inventory.finished_at ? formatDateTime(inventory.finished_at) : ''}
                            />
                        )}
                        <MobileReportBody
                            firstKpis
                            loading={loading}
                            ready={Boolean(summary)}
                            kpis={[
                                { label: 'Total de produtos', icon: 'package', grad: true, value: summary ? Number(summary.total_products || 0).toLocaleString('pt-BR') : '—' },
                                { label: 'Total contado', icon: 'clipcheck', value: summary ? Number(summary.counted || 0).toLocaleString('pt-BR') : '—' },
                                { label: 'Sem divergência', icon: 'check', value: summary ? Number(summary.no_divergence || 0).toLocaleString('pt-BR') : '—' },
                                { label: 'Total divergente', icon: 'alert', value: summary ? Number(summary.divergent || 0).toLocaleString('pt-BR') : '—' },
                            ]}
                            sections={[
                                {
                                    type: 'kpis',
                                    title: 'Ajustes e impacto',
                                    items: [
                                        { label: 'Qtd. ajustada', icon: 'refresh', value: Number(summary?.adjusted_qty || 0).toLocaleString('pt-BR') },
                                        { label: 'Qtd. faltante', icon: 'trenddown', value: `${summary?.shortage_qty ?? 0} un.` },
                                        { label: 'Qtd. excedente', icon: 'trendup', value: `${summary?.surplus_qty ?? 0} un.` },
                                        { label: 'Valor das perdas', icon: 'trenddown', value: formatBRL(summary?.loss_value) },
                                        { label: 'Valor das sobras', icon: 'trendup', value: formatBRL(summary?.gain_value) },
                                        { label: 'Saldo', icon: 'wallet', value: formatBRL(summary?.net_value) },
                                    ],
                                },
                                {
                                    type: 'rank',
                                    title: 'Maiores divergências',
                                    rows: divergent.map(({ item }) => ({
                                        title: item.name,
                                        sub: `${item.difference > 0 ? '+' : '-'} ${Math.abs(item.difference)} un`,
                                        value: signedShort(item.impact, formatMoneyRank),
                                    })),
                                    empty: 'Nenhuma divergência encontrada',
                                },
                                {
                                    type: 'node',
                                    title: 'Produtos',
                                    node: (
                                        <>
                                            <SecH title="Produtos" />
                                            <Caption>
                                                {`${items.length} ${items.length === 1 ? 'produto' : 'produtos'}${onlyDivergent ? ' divergentes' : ''}`}
                                            </Caption>
                                            <Box sx={{ mt: '10px' }}>
                                                <InventoryItemList key={`${id}-${onlyDivergent}`} items={items} formatBRL={formatBRL} />
                                            </Box>
                                        </>
                                    ),
                                },
                            ]}
                        />
                        {!id && (
                            <KitEmpty icon={protoIcon('clipcheck')} title="Nenhum inventário selecionado">
                                Selecione um inventário nos filtros para visualizar o relatório.
                            </KitEmpty>
                        )}
                    </MobileReport>
                )}
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            title="Relatório de Inventário"
            titlePageTarget="Inventário"
            links={[{ title: 'Relatórios', link: '/reports' }]}
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
            message={snackbarMessage}
            severity={snackbarSeverity}
        >
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* page-head — ícone + título + descrição (exportação fica no menu da faixa de filtros). */}
                <Box sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'flex-end' },
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 2.5,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '11px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            background: (t) => t.palette.gradient.brand,
                        }}>
                            <ClipboardList size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                    {inventory ? `Relatório do inventário #${inventory.code}` : 'Relatório de Inventário'}
                                </Typography>
                                {inventory && (
                                    <Chip label={statusInfo.label} size="small" sx={{ bgcolor: statusInfo.bg, color: statusInfo.color, fontWeight: 700 }} />
                                )}
                            </Box>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                {inventory
                                    ? `${inventory.description || 'Sem descrição'}${inventory.location_label ? ` · Local: ${inventory.location_label}` : ''}`
                                    : 'Contagem x estoque do sistema, divergências e impacto financeiro.'}
                            </Typography>
                            {inventory && (
                                <Typography sx={{ fontSize: '12px', color: 'text.disabled', mt: 0.5 }}>
                                    Criado por {inventory.created_by || '—'} em {formatDateTime(inventory.created_at)}
                                    {inventory.finished_at ? ` · Finalizado em ${formatDateTime(inventory.finished_at)}` : ''}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>

                {!canView ? (
                    <EmptyState
                        title="Acesso restrito"
                        subtitle="Você não tem permissão para visualizar o relatório de inventário."
                    />
                ) : (
                    <>
                        {/* Faixa de filtros sem período; carrega sozinha ao escolher o inventário (sem "Gerar"). */}
                        <ReportFilterBar
                            showPeriod={false}
                            showGenerate={false}
                            onClear={() => { setId(''); setOnlyDivergent(false); }}
                            exportItems={[
                                { label: 'PDF', onClick: () => handleExport('pdf'), disabled: !inventory },
                                { label: 'Excel (CSV)', onClick: () => handleExport('csv'), disabled: !inventory },
                            ]}
                        >
                            <Box className="rfb-group">
                                <TbLabel>Inventário</TbLabel>
                                <TextField
                                    select
                                    size="small"
                                    value={id}
                                    onChange={(e) => setId(e.target.value)}
                                    sx={selectFieldSx}
                                    SelectProps={{
                                        displayEmpty: true,
                                        renderValue: (selected) => {
                                            if (selected === '' || selected === undefined) {
                                                return <Typography component="span" sx={{ fontSize: '13px', color: 'text.disabled' }}>Selecione um inventário</Typography>;
                                            }
                                            const inv = inventories.find((i) => i.id === selected);
                                            return inv ? `#${inv.code}${inv.description ? ` - ${inv.description}` : ''}` : '';
                                        },
                                    }}
                                >
                                    {inventories.map((inv) => (
                                        <MenuItem key={inv.id} value={inv.id}>
                                            {`#${inv.code}${inv.description ? ` - ${inv.description}` : ''}`}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>

                            <Box className="rfb-group">
                                <TbLabel>Divergências</TbLabel>
                                <FormControlLabel
                                    sx={{ m: 0, height: 40 }}
                                    control={<Switch checked={onlyDivergent} onChange={(e) => setOnlyDivergent(e.target.checked)} />}
                                    label={<Typography sx={{ fontSize: '13px' }}>Mostrar somente divergentes</Typography>}
                                />
                            </Box>
                        </ReportFilterBar>

                        {/* Chips de contexto. */}
                        {inventory && (
                            <FilterChips sx={{ mb: { xs: 1.5, sm: 3 } }}>
                                <Chip
                                    icon={<Calendar size={13} strokeWidth={2} />}
                                    label={`Criado em ${formatDateTime(inventory.created_at)}`}
                                    size="small"
                                    sx={{ bgcolor: 'info.bg', color: 'primary.main' }}
                                />
                                <Chip
                                    icon={<Package size={13} strokeWidth={2} />}
                                    label={`${items.length} ${items.length === 1 ? 'produto' : 'produtos'}${onlyDivergent ? ' divergentes' : ''}`}
                                    size="small"
                                    sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                                />
                            </FilterChips>
                        )}

                        {!id ? (
                            <EmptyState
                                title="Nenhum inventário selecionado"
                                subtitle="Selecione um inventário para visualizar o relatório."
                            />
                        ) : (
                            <>
                                {/* KPI grid — DESIGN-SYSTEM.md §5.2/§6.1. */}
                                {showKpis && (
                                    <Box sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(5, minmax(0, 1fr))' },
                                        gap: 1.25,
                                        mb: 2,
                                    }}>
                                        <KpiCard icon={<Package size={17} strokeWidth={2} />} iconBg="info.bg" iconFg="primary.main" amount={summary?.total_products} format={plainNumber} label="Total de produtos" loading={loading} />
                                        <KpiCard icon={<ClipboardList size={17} strokeWidth={2} />} iconBg="info.bg" iconFg="primary.main" amount={summary?.counted} format={plainNumber} label="Total contado" loading={loading} />
                                        <KpiCard icon={<Check size={17} strokeWidth={2} />} iconBg="success.bg" iconFg="success.main" amount={summary?.no_divergence} format={plainNumber} label="Sem divergência" loading={loading} />
                                        <KpiCard icon={<AlertTriangle size={17} strokeWidth={2} />} iconBg="warning.bg" iconFg="warning.main" amount={summary?.divergent} format={plainNumber} label="Total divergente" loading={loading} />
                                        <KpiCard icon={<Repeat size={17} strokeWidth={2} />} iconBg="secondary.bg" iconFg="secondary.main" amount={summary?.adjusted_qty} format={plainNumber} label="Qtd. ajustada" loading={loading} />
                                        <KpiCard icon={<TrendingDown size={17} strokeWidth={2} />} iconBg="error.bg" iconFg="error.main" amount={summary?.shortage_qty} format={(n) => `${n} un.`} valueColor="error.main" label="Qtd. faltante" loading={loading} />
                                        <KpiCard icon={<TrendingUp size={17} strokeWidth={2} />} iconBg="success.bg" iconFg="success.main" amount={summary?.surplus_qty} format={(n) => `${n} un.`} valueColor="success.main" label="Qtd. excedente" loading={loading} />
                                        <KpiCard icon={<TrendingDown size={17} strokeWidth={2} />} iconBg="error.bg" iconFg="error.main" {...moneyKpiProps(summary?.loss_value)} valueColor="error.main" label="Valor das perdas" loading={loading} />
                                        <KpiCard icon={<TrendingUp size={17} strokeWidth={2} />} iconBg="success.bg" iconFg="success.main" {...moneyKpiProps(summary?.gain_value)} valueColor="success.main" label="Valor das sobras" loading={loading} />
                                        <KpiCard icon={<Wallet size={17} strokeWidth={2} />} iconBg="info.bg" iconFg="primary.main" {...moneyKpiProps(summary?.net_value)} valueColor={netColor} label="Saldo" loading={loading} />
                                    </Box>
                                )}

                                <DefaultTable
                                    colunms={columns}
                                    data={items}
                                    loading={loading}
                                    rowPerPage={15}
                                    rowsPerPageOptions={[15, 30, 50]}
                                    itemLabel="produtos"
                                    sortableFields={['code', 'name', 'expected_qty', 'counted_qty', 'difference']}
                                    defaultSort={{ field: 'name', direction: 'asc' }}
                                />
                            </>
                        )}
                    </>
                )}
            </Box>
        </PageScaffold>
    );
}
